import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbDropdown, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { ApplicationInsightsService } from '../../services/application-insights/application-insights.service';
import { DialogHelper } from '../../services/dialog-helper';
import { GlobalErrorHandler } from '../../services/global-error-handler';
import { IsBusyService } from '../../services/is-busy/is-busy.service';
import { UnitOfWorkService } from '../../services/unit-of-work/unit-of-work.service';
import * as model from '../../../model/model';
import * as modelDto from '../../../model/modelDto';
import { InspectionQuestionType } from '../../../model/modelEnums';

const loadingKey = 'admin:inspectionQuestion:loadData';
const savingKey = 'admin:inspectionQuestion:save';

const activityFieldLabels: { [fieldName: string]: string } = {
    QuestionCode: 'Question Code',
    QuestionText: 'Question',
    HelpText: 'Help Text',
    QuestionType: 'Question Type',
    StandardId: 'Standard',
    BranchingQuestionId: 'Branching Question',
    InspectionQuestionOptions: 'Answer Options',
    InspectionQuestionTopics: 'Topics'
};

interface InspectionQuestionFormState {
    id: number;
    questionCode: string;
    questionText: string;
    helpText: string | null;
    standardId: number | null;
    topicIds: number[];
    questionType: InspectionQuestionType;
    options: InspectionQuestionOptionFormState[];
    isActive: boolean;
}

interface InspectionQuestionOptionFormState {
    id: number;
    optionText: string;
    branchingQuestionId: number | null;
}

interface QuestionActivityLogFilterState {
    user: string;
    fieldName: string;
    oldValue: string;
    newValue: string;
    changedFromUtc: string;
    changedToUtc: string;
}

@Component({
    selector: 'app-inspection-question-editor',
    templateUrl: './inspection-question-editor.component.html',
    standalone: false
})
export class InspectionQuestionEditorComponent implements OnInit {
    @ViewChild('activityLogModal') activityLogModal: TemplateRef<any>;

    question: InspectionQuestionFormState = this.newQuestion();
    topics: model.Topic[] = [];
    standards: model.Standard[] = [];
    branchingQuestions: model.InspectionQuestion[] = [];
    activityLog: modelDto.InspectionQuestionChangeDto[] = [];
    activityLogFilters = this.emptyActivityLogFilters();
    draftActivityLogFilters = this.emptyActivityLogFilters();
    activityLogDateFilterError = '';
    readonly activityLogFieldOptions = Object.keys(activityFieldLabels)
        .map(fieldName => ({ value: fieldName, label: activityFieldLabels[fieldName] }));
    readonly questionTypes = [
        { value: InspectionQuestionType.SingleSelect, label: 'Single Select' },
        { value: InspectionQuestionType.MultipleSelect, label: 'Multiple Select' },
        { value: InspectionQuestionType.Date, label: 'Date' },
        { value: InspectionQuestionType.Text, label: 'Text' }
    ];
    isLoading = true;
    isSaving = false;
    isCheckingCode = false;
    questionCodeDuplicate = false;
    isLoadingActivityLog = false;
    activityLogLoadError = false;

    private originalState: string = null;
    private originalTopicIds: number[] = [];
    private originalStandardId: number = null;
    private lastValidStandardId: number = null;
    private lastQuestionType = InspectionQuestionType.Text;
    private codeCheckSequence = 0;

    constructor(
        private activeRoute: ActivatedRoute,
        private uow: UnitOfWorkService,
        private appInsights: ApplicationInsightsService,
        private errorHandler: GlobalErrorHandler,
        private router: Router,
        public isBusy: IsBusyService,
        public dialogHelper: DialogHelper,
        private modalService: NgbModal
    ) {
        appInsights.logPageView();
    }

    ngOnInit() {
        this.activeRoute.queryParams.subscribe(routeParams => {
            this.loadQuestion(routeParams.id);
        });
    }

    get hasChanges() {
        return !this.isLoading && this.originalState !== null && this.serializeState(this.question) !== this.originalState;
    }

    get sortedTopics() {
        return this.topics.slice().sort((a, b) => a.name.localeCompare(b.name));
    }

    get sortedStandards() {
        return this.standards.slice().sort((a, b) => a.name.localeCompare(b.name));
    }

    get sortedBranchingQuestions() {
        return this.branchingQuestions.slice().sort((a, b) =>
            (a.questionCode || '').localeCompare(b.questionCode || '') ||
            (a.questionText || '').localeCompare(b.questionText || '')
        );
    }

    get showAnswerOptions() {
        return this.isSelectType(this.question.questionType);
    }

    get optionsValid() {
        if (!this.showAnswerOptions) {
            return true;
        }

        const options = this.question.options || [];
        return options.length >= 2 &&
            options.every(option => !!(option.optionText || '').trim() && (option.optionText || '').length <= 500) &&
            options.every((_, index) => !this.isDuplicateOption(index));
    }

    canDeactivate(): Observable<boolean> | boolean {
        if (!this.hasChanges) {
            return true;
        }

        const proceed = this.dialogHelper.confirm();
        proceed.subscribe(result => {
            if (result) {
                this.undoChanges();
            }
        });
        return proceed;
    }

    undoChanges() {
        if (this.originalState !== null) {
            this.question = JSON.parse(this.originalState);
            this.lastQuestionType = this.question.questionType;
        }
    }

    goHome() {
        this.router.navigate(['../questions'], { relativeTo: this.activeRoute });
    }

    openActivityLog() {
        if (this.question.id <= 0 || this.isLoadingActivityLog) {
            return;
        }

        this.resetActivityLogFilters();
        this.modalService.open(this.activityLogModal, {
            size: 'xl',
            scrollable: true,
            ariaLabelledBy: 'inspection-question-activity-log-title'
        });

        this.loadActivityLog();
    }

    onActivityLogFilterOpen(isOpen: boolean) {
        if (isOpen) {
            this.draftActivityLogFilters = { ...this.activityLogFilters };
            this.activityLogDateFilterError = '';
        }
    }

    applyActivityLogFilters(dropdown?: NgbDropdown) {
        if (!this.activityLogDateRangeIsValid()) {
            return;
        }

        this.activityLogFilters = { ...this.draftActivityLogFilters };
        dropdown?.close();
        this.loadActivityLog();
    }

    clearActivityLogFilter(field: keyof QuestionActivityLogFilterState) {
        this.draftActivityLogFilters = {
            ...this.draftActivityLogFilters,
            [field]: null
        };
        this.activityLogDateFilterError = '';
    }

    clearActivityLogFilters(dropdown?: NgbDropdown) {
        this.resetActivityLogFilters();
        dropdown?.close();
        this.loadActivityLog();
    }

    get hasActivityLogFilters() {
        return Object.values(this.activityLogFilters).some(value => !!value);
    }

    private loadActivityLog() {
        this.activityLog = [];
        this.activityLogLoadError = false;
        this.isLoadingActivityLog = true;
        const filters = this.buildActivityLogFilters();
        const request = filters.length
            ? this.uow.getInspectionQuestionChanges(this.question.id, JSON.stringify(filters))
            : this.uow.getInspectionQuestionChanges(this.question.id);

        request
            .then(result => {
                this.activityLog = result || [];
            })
            .catch(reason => {
                this.activityLogLoadError = true;
                this.errorHandler.handleError(reason);
            })
            .finally(() => {
                this.isLoadingActivityLog = false;
            });
    }

    private buildActivityLogFilters() {
        const filters: modelDto.GridFilter[] = [];
        const values = this.activityLogFilters;
        const add = (key: string, value: string) => {
            if (value?.trim()) {
                filters.push({ key, value: value.trim() } as modelDto.GridFilter);
            }
        };

        add('user', values.user);
        add('fieldName', values.fieldName);
        add('oldValue', values.oldValue);
        add('newValue', values.newValue);
        if (values.changedFromUtc) {
            add('changedFromUtc', this.toUtcFilterValue(values.changedFromUtc));
        }
        if (values.changedToUtc) {
            add('changedToUtc', this.toUtcFilterValue(values.changedToUtc));
        }
        return filters;
    }

    private activityLogDateRangeIsValid() {
        const from = this.draftActivityLogFilters.changedFromUtc;
        const to = this.draftActivityLogFilters.changedToUtc;
        if (from && to && from > to) {
            this.activityLogDateFilterError = 'Changed From must be before or equal to Changed To.';
            return false;
        }

        this.activityLogDateFilterError = '';
        return true;
    }

    private toUtcFilterValue(value: string) {
        return new Date(`${value}Z`).toISOString();
    }

    private resetActivityLogFilters() {
        this.activityLogFilters = this.emptyActivityLogFilters();
        this.draftActivityLogFilters = this.emptyActivityLogFilters();
        this.activityLogDateFilterError = '';
    }

    private emptyActivityLogFilters(): QuestionActivityLogFilterState {
        return {
            user: null,
            fieldName: null,
            oldValue: null,
            newValue: null,
            changedFromUtc: null,
            changedToUtc: null
        };
    }

    getActivityLogUser(change: modelDto.InspectionQuestionChangeDto) {
        const name = (change.employeeName || '').trim();
        return name || change.employeeId;
    }

    getActivityLogFieldName(change: modelDto.InspectionQuestionChangeDto) {
        return activityFieldLabels[change.fieldName] || change.fieldName;
    }

    getActivityLogValue(value: string | null, fieldName?: string) {
        if (!value || value.trim() === '') {
            return 'None';
        }

        if (fieldName === 'StandardId') {
            return this.getActivityLogLookupValue(value, this.standards);
        }

        if (fieldName === 'InspectionQuestionTopics') {
            return this.formatActivityLogTopics(value);
        }

        if (fieldName !== 'InspectionQuestionOptions') {
            return value;
        }

        try {
            const options = JSON.parse(value);
            if (!Array.isArray(options)) {
                return value;
            }

            if (options.length === 0) {
                return 'None';
            }

            return options
                .map((option, index) => {
                    const optionText = typeof option === 'string'
                        ? option
                        : option?.OptionText || option?.optionText || '';
                    return `${index + 1}. ${optionText || 'None'}`;
                })
                .join(', ');
        } catch {
            return value;
        }
    }

    private formatActivityLogTopics(value: string) {
        try {
            const topics = JSON.parse(value);
            if (!Array.isArray(topics)) {
                return value;
            }

            if (topics.length === 0) {
                return 'None';
            }

            return topics
                .map((topic, index) => {
                    const topicName = topic?.TopicName ||
                        topic?.topicName ||
                        this.getActivityLogLookupValue(
                            topic?.TopicId ?? topic?.topicId,
                            this.topics);
                    return `${index + 1}. ${topicName || 'None'}`;
                })
                .join(', ');
        } catch {
            return value;
        }
    }

    private getActivityLogLookupValue(value: string | number, items: { id: number; name: string }[]) {
        if (value === null || value === undefined) {
            return 'None';
        }

        const textValue = value.toString().trim();
        if (!textValue) {
            return 'None';
        }

        const id = Number(textValue);
        if (!Number.isNaN(id) && /^\d+$/.test(textValue)) {
            const item = items.find(candidate => candidate.id === id);
            return item?.name || 'None';
        }

        return textValue;
    }

    formatActivityLogDate(value: Date | string) {
        if (!value) {
            return '';
        }

        const rawValue = value instanceof Date ? value.toISOString() : value;
        const utcValue = /(?:Z|[+-]\d{2}:\d{2})$/.test(rawValue) ? rawValue : `${rawValue}Z`;
        const date = new Date(utcValue);
        if (Number.isNaN(date.getTime())) {
            return '';
        }

        const pad = (part: number) => part.toString().padStart(2, '0');
        return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())} ` +
            `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())} UTC`;
    }

    onQuestionCodeChanged() {
        this.codeCheckSequence++;
        this.isCheckingCode = false;
        this.questionCodeDuplicate = false;
    }

    async checkQuestionCode() {
        const questionCode = (this.question.questionCode || '').trim();
        if (!questionCode || questionCode.length > 50) {
            this.questionCodeDuplicate = false;
            return false;
        }

        const sequence = ++this.codeCheckSequence;
        this.isCheckingCode = true;
        try {
            const result = await this.uow.inspectionQuestionCodeExists(
                questionCode,
                this.question.id > 0 ? this.question.id : null
            );

            if (sequence === this.codeCheckSequence) {
                this.questionCodeDuplicate = !!result[0];
            }
            return !result[0];
        } catch (reason) {
            if (sequence === this.codeCheckSequence) {
                this.errorHandler.handleError(reason);
            }
            return false;
        } finally {
            if (sequence === this.codeCheckSequence) {
                this.isCheckingCode = false;
            }
        }
    }

    isTopicDisabled(topic: model.Topic) {
        return !topic.isActive && !this.originalTopicIds.includes(topic.id);
    }

    isStandardDisabled(standard: model.Standard) {
        return !standard.isActive && standard.id !== this.originalStandardId;
    }

    onTopicsChanged() {
        this.question.topicIds = (this.question.topicIds || [])
            .filter(id => {
                const topic = this.topics.find(item => item.id === id);
                return topic && (topic.isActive || this.originalTopicIds.includes(id));
            });
    }

    onStandardChanged() {
        const standard = this.standards.find(item => item.id === this.question.standardId);
        if (standard && this.isStandardDisabled(standard)) {
            this.question.standardId = this.lastValidStandardId;
            return;
        }

        this.lastValidStandardId = this.question.standardId;
    }

    onQuestionTypeChanged(questionType: InspectionQuestionType) {
        const previousType = this.lastQuestionType;
        if (questionType === previousType) {
            return;
        }

        if (this.isSelectType(previousType) &&
            !this.isSelectType(questionType) &&
            this.question.options.length > 0) {
            const proceed = confirm('Changing the Question Type will remove all answer options when saved. Continue?');
            if (!proceed) {
                this.question.questionType = previousType;
                return;
            }
            this.question.options = [];
        }

        this.lastQuestionType = questionType;
        if (this.isSelectType(questionType) && this.question.options.length === 0) {
            this.addAnswerOption();
        }
    }

    addAnswerOption() {
        this.question.options.push({
            id: 0,
            optionText: '',
            branchingQuestionId: null
        });
    }

    deleteAnswerOption(index: number) {
        if (!confirm('Are you sure you want to remove this answer option?')) {
            return;
        }
        this.question.options.splice(index, 1);
    }

    isDuplicateOption(index: number) {
        const optionText = (this.question.options[index]?.optionText || '').trim().toLocaleLowerCase();
        if (!optionText) {
            return false;
        }
        return this.question.options.some((option, optionIndex) =>
            optionIndex !== index && (option.optionText || '').trim().toLocaleLowerCase() === optionText
        );
    }

    isBranchingQuestionDisabled(question: model.InspectionQuestion, option: InspectionQuestionOptionFormState) {
        return this.isBranchingQuestionInactive(question) && question.id !== option.branchingQuestionId;
    }

    isBranchingQuestionInactive(question: model.InspectionQuestion) {
        return !question.isActive || question.isDeleted;
    }

    onBranchingQuestionChanged(option: InspectionQuestionOptionFormState) {
        if (option.branchingQuestionId === this.question.id) {
            option.branchingQuestionId = null;
            return;
        }

        const question = this.branchingQuestions.find(item => item.id === option.branchingQuestionId);
        if (question && this.isBranchingQuestionDisabled(question, option)) {
            option.branchingQuestionId = null;
        }
    }

    async saveChanges(): Promise<boolean> {
        if (this.isSaving) {
            return false;
        }

        const questionCode = (this.question.questionCode || '').trim();
        const questionText = this.question.questionText || '';
        if (!questionCode ||
            questionCode.length > 50 ||
            !questionText.trim() ||
            !(this.question.topicIds || []).length ||
            !this.optionsValid) {
            return false;
        }

        const codeIsUnique = await this.checkQuestionCode();
        if (!codeIsUnique || this.questionCodeDuplicate) {
            return false;
        }

        this.isSaving = true;
        this.isBusy.set(savingKey, true, 'Saving question');
        try {
            const dto: modelDto.InspectionQuestionSaveDto = {
                id: this.question.id,
                questionCode: questionCode,
                questionText: questionText,
                helpText: this.question.helpText && this.question.helpText.trim() ? this.question.helpText : null,
                standardId: this.question.standardId,
                topicIds: this.question.topicIds.slice(),
                questionType: this.question.questionType,
                options: this.showAnswerOptions
                    ? this.question.options.map((option, index) => ({
                        id: option.id,
                        optionText: option.optionText.trim(),
                        displayOrder: index + 1,
                        branchingQuestionId: option.branchingQuestionId
                    }))
                    : []
            };
            const result = await this.uow.saveInspectionQuestion(dto);
            const savedQuestion = result[0];
            if (!savedQuestion || !savedQuestion.id) {
                throw new Error('The saved question was not returned by the server.');
            }

            const wasNew = this.question.id === 0;
            this.question = this.mapQuestion(savedQuestion, dto.topicIds);
            this.setOriginalState();

            if (wasNew) {
                await this.router.navigate(['/admin/question'], {
                    queryParams: { id: savedQuestion.id },
                    replaceUrl: true
                });
            }
            return true;
        } catch (reason) {
            this.errorHandler.handleError(reason);
            return false;
        } finally {
            this.isSaving = false;
            this.isBusy.set(savingKey, false);
        }
    }

    private async loadQuestion(routeId: string) {
        const isNew = routeId === 'new';
        const id = Number.parseInt(routeId, 10);
        if (!isNew && (!routeId || Number.isNaN(id) || id < 1 || id.toString() !== routeId.toString())) {
            this.goHome();
            return;
        }

        this.isLoading = true;
        this.originalState = null;
        this.isBusy.set(loadingKey, true, 'Loading question');
        try {
            const requests: Promise<any[]>[] = [
                this.uow.getTopics(null, 1, 500),
                this.uow.getStandards(null, 1, 500),
                this.uow.getInspectionQuestionBranchingChoices(isNew ? null : id)
            ];
            if (!isNew) {
                requests.push(this.uow.getInspectionQuestion(id));
            }

            const [topics, standards, branchingQuestions, questionResult] = await Promise.all(requests);
            this.topics = topics;
            this.standards = standards;

            if (isNew) {
                this.question = this.newQuestion();
            } else {
                const loadedQuestion = questionResult && questionResult[0];
                if (!loadedQuestion) {
                    this.goHome();
                    return;
                }
                this.question = this.mapQuestion(loadedQuestion);
            }

            this.setBranchingQuestions(branchingQuestions, isNew ? null : questionResult[0]);
            this.setOriginalState();
        } catch (reason) {
            this.errorHandler.handleError(reason);
            this.goHome();
        } finally {
            this.isLoading = false;
            this.isBusy.set(loadingKey, false);
        }
    }

    private mapQuestion(question: model.InspectionQuestion, fallbackTopicIds: number[] = []): InspectionQuestionFormState {
        const mappedTopicIds = question.inspectionQuestionTopics
            ? question.inspectionQuestionTopics
                .filter(item => !item.isDeleted)
                .map(item => item.topicId)
            : [];
        const topicIds = mappedTopicIds.length ? mappedTopicIds : fallbackTopicIds;

        return {
            id: question.id,
            questionCode: question.questionCode || '',
            questionText: question.questionText || '',
            helpText: question.helpText,
            standardId: question.standardId,
            topicIds: topicIds.slice(),
            questionType: this.normalizeQuestionType(question.questionType),
            options: (question.inspectionQuestionOptions || [])
                .filter(option => !option.isDeleted)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map(option => ({
                    id: option.id,
                    optionText: option.optionText || '',
                    branchingQuestionId: option.branchingQuestionId === question.id ? null : option.branchingQuestionId
                })),
            isActive: question.isActive
        };
    }

    private setBranchingQuestions(
        activeQuestions: model.InspectionQuestion[] = [],
        loadedQuestion: model.InspectionQuestion = null
    ) {
        const choices = new Map<number, model.InspectionQuestion>();
        (activeQuestions || [])
            .filter(question =>
                question.id !== this.question.id &&
                question.isActive &&
                !question.isDeleted
            )
            .forEach(question => choices.set(question.id, question));

        (loadedQuestion?.inspectionQuestionOptions || [])
            .filter(option =>
                !option.isDeleted &&
                option.branchingQuestion &&
                option.branchingQuestion.id !== this.question.id
            )
            .forEach(option => {
                if (!choices.has(option.branchingQuestion.id)) {
                    choices.set(option.branchingQuestion.id, option.branchingQuestion);
                }
            });

        this.branchingQuestions = Array.from(choices.values());
    }

    private normalizeQuestionType(questionType: InspectionQuestionType | string): InspectionQuestionType {
        switch (String(questionType)) {
            case '0':
            case 'SingleSelect':
                return InspectionQuestionType.SingleSelect;
            case '1':
            case 'MultipleSelect':
                return InspectionQuestionType.MultipleSelect;
            case '2':
            case 'Date':
                return InspectionQuestionType.Date;
            case '3':
            case 'Text':
                return InspectionQuestionType.Text;
            default:
                return InspectionQuestionType.Text;
        }
    }

    private setOriginalState() {
        this.originalTopicIds = this.question.topicIds.slice();
        this.originalStandardId = this.question.standardId;
        this.lastValidStandardId = this.question.standardId;
        this.lastQuestionType = this.question.questionType;
        this.originalState = this.serializeState(this.question);
        this.questionCodeDuplicate = false;
    }

    private serializeState(state: InspectionQuestionFormState) {
        return JSON.stringify({
            ...state,
            topicIds: (state.topicIds || []).slice().sort((a, b) => a - b)
        });
    }

    private newQuestion(): InspectionQuestionFormState {
        return {
            id: 0,
            questionCode: '',
            questionText: '',
            helpText: null,
            standardId: null,
            topicIds: [],
            questionType: InspectionQuestionType.Text,
            options: [],
            isActive: true
        };
    }

    private isSelectType(questionType: InspectionQuestionType) {
        return questionType === InspectionQuestionType.SingleSelect ||
            questionType === InspectionQuestionType.MultipleSelect;
    }
}
